import { distanceM } from '../geo/distance.js';
import { FAHRBARE_ARTEN } from './types.js';
/**
 * Der Treffpunkt, an dem dieses Fahrzeug **jetzt** ankommt — oder `null`.
 *
 * ## Warum nur einer
 *
 * Eine Runde beginnt und endet oft am selben Ort: Start und Ziel liegen
 * übereinander, beide im selben Umkreis. Würden beide zugleich vermerkt, stünde
 * am Morgen „drei sind schon am Ziel" auf dem Bildschirm des Führungsfahrzeugs.
 *
 * Deshalb gilt von mehreren Punkten im Umkreis **der früheste, der noch fehlt**.
 * Am Morgen ist das der Start; am Abend ist der Start längst vermerkt, und es
 * bleibt das Ziel. Die Reihenfolge ist die der Route, also `seq`.
 *
 * ## Warum kein Halten verlangt wird
 *
 * Wer durch den Umkreis fährt, ohne zu halten, gilt als angekommen. Das ist
 * Absicht: Die Frage der Führung am Sammelpunkt lautet „wer fehlt noch?", und
 * wer vorbeigefahren ist, fehlt nicht — er ist vorn. Eine zusätzliche Bedingung
 * „und stand dort eine Minute" würde genau den übersehen, der als Erster da war
 * und schon wieder losgefahren ist.
 *
 * ## Warum Stützpunkte nicht zählen
 *
 * Sie formen die Route, niemand hält dort, und der Fahrer sieht sie nicht. Eine
 * Ankunft an einer Kreuzung ist keine Auskunft, sondern Rauschen — und
 * personenbezogenes obendrein.
 */
export function ankunftJetzt(eingabe) {
    let bester = null;
    for (const waypoint of eingabe.waypoints) {
        if (!FAHRBARE_ARTEN.includes(waypoint.kind) || eingabe.bereits.has(waypoint.id)) {
            continue;
        }
        const umkreis = waypoint.radiusM ?? eingabe.radiusM;
        if (distanceM(eingabe.position, waypoint) > umkreis) {
            continue;
        }
        if (bester === null || waypoint.seq < bester.seq) {
            bester = waypoint;
        }
    }
    return bester;
}
/**
 * Wartet die Führung gerade an einem Treffpunkt?
 *
 * Davon hängt ab, ob ein Rückstand ein Ereignis ist. Wer zwanzig Minuten
 * hinterherkommt, reißt jede Schwelle — aber die Führung weiß das längst, sie
 * wartet ja auf ihn. **Ein erwartetes Warten ist kein Ereignis** (Entwurf,
 * Abschnitt 7).
 *
 * Gemessen an der **aktuellen Position** der Führung und nicht an einer
 * aufgezeichneten Ankunft: Sie sagt, ob gerade gewartet wird; eine Aufzeichnung
 * sagt nur, dass jemand einmal dort war.
 *
 * Nur an Punkten, an denen überhaupt gewartet wird. Am Start und am Ziel gilt
 * dasselbe: Dort steht die Kolonne zusammen, bevor es losgeht, und danach.
 */
export function fuehrungWartet(leaderPosition, waypoints, radiusM) {
    if (leaderPosition === null) {
        return false;
    }
    return waypoints.some((w) => FAHRBARE_ARTEN.includes(w.kind) &&
        distanceM(leaderPosition, w) <= (w.radiusM ?? radiusM));
}
/**
 * An welchem Treffpunkt steht dieses Fahrzeug gerade? — `null`, wenn an keinem.
 *
 * Für die Anzeige, nicht für das Festhalten: Das entscheidet der Server, und
 * diese Antwort folgt ihm. Liegt eine eigene Ankunft für einen Punkt im Umkreis
 * vor, gilt der — auch wenn ein zweiter näher läge. Sonst derselbe Vorrang wie
 * beim Festhalten: der früheste auf der Route.
 *
 * Der Fall dahinter ist wieder die Runde, die am selben Marktplatz beginnt und
 * endet. Am Morgen steht dort „Start", am Abend „Ziel" — und zwar deshalb, weil
 * der Server es so vermerkt hat und nicht, weil die Oberfläche es errät.
 */
export function treffpunktHier(position, waypoints, radiusM, eigeneAnkuenfte) {
    const imUmkreis = waypoints.filter((w) => FAHRBARE_ARTEN.includes(w.kind) && distanceM(position, w) <= (w.radiusM ?? radiusM));
    const vermerkt = imUmkreis.filter((w) => eigeneAnkuenfte.has(w.id));
    const auswahl = vermerkt.length > 0 ? vermerkt : imUmkreis;
    let bester = null;
    for (const w of auswahl) {
        if (bester === null || w.seq < bester.seq) {
            bester = w;
        }
    }
    return bester;
}
/**
 * „Wer ist schon da?" — für die Führung am Sammelpunkt die einzige Frage, die
 * zählt (Entwurf, Abschnitt 6).
 *
 * ## Wer als fehlend gilt
 *
 * Alle Teilnehmer der Ausfahrt außer denen, deren Ankunft an diesem Punkt
 * vermerkt ist. **Auch wer gar nicht mehr meldet.** Das ist der Kern der Frage:
 * Ein Fahrzeug ohne Signal ist gerade das, auf das gewartet wird, und es aus
 * der Liste zu nehmen hieße, die Kolonne ohne es losfahren zu lassen.
 *
 * Wer sich unsichtbar geschaltet oder die Ausfahrt verlassen hat, gehört
 * dagegen nicht mehr dazu — der Aufrufer übergibt, wer noch mitfährt.
 *
 * Die Reihenfolge der Ankömmlinge ist die ihrer Ankunft; die Fehlenden bleiben
 * in der Reihenfolge der übergebenen Liste.
 */
export function anwesenheit(teilnehmer, arrivals, waypointId) {
    const dabei = new Set(teilnehmer);
    const da = arrivals
        .filter((a) => a.waypointId === waypointId && dabei.has(a.participantId))
        .sort((a, b) => a.arrivedAt - b.arrivedAt)
        .map((a) => a.participantId);
    const schonDa = new Set(da);
    return { da, fehlen: teilnehmer.filter((id) => !schonDa.has(id)) };
}
//# sourceMappingURL=arrival.js.map